import React from 'react';

import { Box, Typography } from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';

import strings from '../../../localization';
import { ButtonSize, Constants } from '../../../helpers/Constants';
import { CameraCaptureError } from '../../../helpers/cameraCapture';
import useCameraCapture from '../../../hooks/CameraCapture/useCameraCapture';

import CustomButton from '../CustomButton';
import styles from './styles';

interface ICameraCapture {
  onCancel: () => void;
  onCapture: (file: File) => void;
}

const getCameraErrorMessages = (): Record<CameraCaptureError, string> =>
  ({
    [CameraCaptureError.permissionDenied]: strings.cameraPermissionDenied,
    [CameraCaptureError.unavailable]: strings.cameraUnavailable,
    [CameraCaptureError.captureFailed]: strings.cameraCaptureFailed,
  });

const CameraCapture = ({ onCancel, onCapture }: ICameraCapture) => {
  const {
    cameraError,
    canCapture,
    handleCapture,
    handleVideoReady,
    videoRef,
  } = useCameraCapture({ onCapture });

  const errorMessage = cameraError
    ? getCameraErrorMessages()[cameraError]
    : null;

  return (
    <Box sx={styles.root}>
      <Box
        component="video"
        ref={videoRef}
        aria-label={strings.cameraPreview}
        autoPlay
        muted
        playsInline
        onLoadedData={handleVideoReady}
        sx={styles.video}
      />

      {errorMessage && (
        <Typography role="alert" sx={styles.error}>
          {errorMessage}
        </Typography>
      )}

      <Box sx={styles.actions}>
        <CustomButton
          label={strings.cancel}
          type="button"
          size={ButtonSize.small}
          variant={Constants.variant.outlined}
          fullWidth={false}
          onClick={onCancel}
        />
        <CustomButton
          label={strings.capturePhoto}
          type="button"
          size={ButtonSize.small}
          fullWidth={false}
          startIcon={<PhotoCameraOutlinedIcon />}
          disabled={!canCapture}
          onClick={handleCapture}
        />
      </Box>
    </Box>
  );
};

export default CameraCapture;
